import React, { useEffect, useState } from "react";
import { AlertCircle, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "qit:storage-access-dismissed";
const TEST_KEY = "__qit_storage_test__";
const TEST_COOKIE = "__qit_cookie_test";

type StorageAccessIssue = "localStorage" | "sessionStorage" | "cookies";

const canUseStorage = (storage: Storage | undefined): boolean => {
    if (!storage) return false;
    try {
        storage.setItem(TEST_KEY, "1");
        storage.removeItem(TEST_KEY);
        return true;
    } catch {
        return false;
    }
};

const canUseCookies = (): boolean => {
    if (typeof document === "undefined" || !navigator.cookieEnabled) return false;
    try {
        document.cookie = `${TEST_COOKIE}=1; path=/; max-age=60; SameSite=Lax`;
        const exists = document.cookie.split(";").some((part) => part.trim().startsWith(`${TEST_COOKIE}=`));
        document.cookie = `${TEST_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
        return exists;
    } catch {
        return false;
    }
};

const getStorageAccessIssues = (): StorageAccessIssue[] => {
    if (typeof window === "undefined") return [];

    const issues: StorageAccessIssue[] = [];
    try {
        if (!canUseStorage(window.localStorage)) issues.push("localStorage");
    } catch {
        issues.push("localStorage");
    }
    try {
        if (!canUseStorage(window.sessionStorage)) issues.push("sessionStorage");
    } catch {
        issues.push("sessionStorage");
    }
    if (!canUseCookies()) issues.push("cookies");
    return issues;
};

const hasDismissedPrompt = (): boolean => {
    try {
        return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
        return false;
    }
};

const StorageAccessPrompt: React.FC = () => {
    const [issues, setIssues] = useState<StorageAccessIssue[]>([]);
    const [dismissed, setDismissed] = useState(false);

    const checkAccess = () => {
        setDismissed(hasDismissedPrompt());
        setIssues(getStorageAccessIssues());
    };

    useEffect(() => {
        checkAccess();
    }, []);

    const dismiss = () => {
        setDismissed(true);
        try {
            window.sessionStorage.setItem(DISMISS_KEY, "1");
        } catch {
            // If storage is blocked, keep the dismissal in component state for this page view.
        }
    };

    if (dismissed || issues.length === 0) return null;

    const cookieBlocked = issues.includes("cookies");
    const storageBlocked = issues.includes("localStorage") || issues.includes("sessionStorage");

    return (
        <div className="fixed inset-x-3 bottom-3 z-[1000] mx-auto max-w-xl rounded-md border border-amber-300 bg-background p-3 text-foreground shadow-lg">
            <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">Allow cookies and site data</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {cookieBlocked && storageBlocked
                            ? "Your browser is blocking cookies or site storage. Sessions need both to keep sign-in and invite links working."
                            : cookieBlocked
                              ? "Your browser is blocking cookies. Allow cookies for this site, then try again."
                              : "Your browser is blocking site storage. Allow website data for this site, then try again."}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        Use a normal browser tab, not Private/Incognito. In browser settings, allow cookies and site data for Qit.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" onClick={checkAccess}>
                            <RefreshCw className="h-4 w-4" aria-hidden="true" />
                            Try again
                        </Button>
                        <Button size="sm" variant="outline" onClick={dismiss}>
                            Continue
                        </Button>
                    </div>
                </div>
                <button
                    type="button"
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={dismiss}
                    aria-label="Dismiss storage access warning"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export default StorageAccessPrompt;
